import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import useApi from '../hooks/useApi';
import { buildCalendarDateMeta } from '../utils/dateUtils';
import AppSheetMrtTable from './AppSheetMrtTable';
import { PROGRAM_OPTIONS } from './dashboard/constants';
import { useCancelsReport } from '../hooks/useCancelsReport';
import CancelsMetricCard from './cancels/CancelsMetricCard';

const SOURCE_OPTIONS = [
  { value: 'synthetic', label: 'Synthetic' },
  { value: 'status', label: 'Status' },
  { value: 'all', label: 'All' },
];

export const buildCancelRows = (data) => {
  const list = Array.isArray(data?.rows) ? data.rows : [];
  return list.map((row, index) => {
    const cancelMeta = buildCalendarDateMeta(row?.cancelDate);
    return {
      ...row,
      __rowId: `${row?.programName || 'program'}:${row?.bac || 'bac'}:${row?.cancelDate || index}`,
      cancelDateDisplay: cancelMeta.display || '',
      cancelDateSort: cancelMeta.sort,
    };
  });
};

const CancelsReport = () => {
  const { useCancelledBacsReport } = useApi();
  const {
    programName,
    source,
    includeAppsheet,
    pagination,
    setPagination,
    handleProgramChange,
    handleSourceChange,
    toggleAppsheet,
    resolvedProgram,
    offset,
  } = useCancelsReport();

  const { data, isLoading, error } = useCancelledBacsReport({
    programName: resolvedProgram,
    source,
    includeAppsheet,
    limit: pagination.pageSize,
    offset,
  });

  const rows = useMemo(() => buildCancelRows(data), [data]);

  const columns = useMemo(
    () => [
      { id: 'bac', header: 'BAC', accessorKey: 'bac', size: 120 },
      { id: 'programName', header: 'Program', accessorKey: 'programName', size: 180 },
      { id: 'cancelDateDisplay', header: 'Cancel Date', accessorKey: 'cancelDateDisplay', size: 180 },
      { id: 'cancellationSource', header: 'Source', accessorKey: 'cancellationSource', size: 140 },
      { id: 'appsheetAccountNamesCsv', header: 'AppSheet Account Names', accessorKey: 'appsheetAccountNamesCsv', size: 280 },
      { id: 'appsheetAccountIdsCsv', header: 'AppSheet Account IDs', accessorKey: 'appsheetAccountIdsCsv', size: 220 },
    ],
    []
  );

  const summary = data?.summary || {};
  const rowCount = Number.isFinite(data?.pagination?.total) ? data.pagination.total : summary.total || rows.length;
  const warnings = Array.isArray(data?.warnings) ? data.warnings : [];

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5">Cancels</Typography>
          <Typography variant="body2" color="text.secondary">
            Cancelled BACs with optional AppSheet account lookups.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Program</InputLabel>
            <Select
              value={programName}
              label="Program"
              onChange={(event) => {
                handleProgramChange(event.target.value);
              }}
            >
              {PROGRAM_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>Source</InputLabel>
            <Select
              value={source}
              label="Source"
              onChange={(event) => {
                handleSourceChange(event.target.value);
              }}
            >
              {SOURCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip
            label={includeAppsheet ? 'AppSheet: On' : 'AppSheet: Off'}
            color={includeAppsheet ? 'primary' : 'default'}
            variant={includeAppsheet ? 'filled' : 'outlined'}
            onClick={toggleAppsheet}
          />
        </Box>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.response?.data?.message || error?.message || 'Failed to load cancels report.'}
        </Alert>
      )}

      {!isLoading && !error && (
        <>
          {data?.cache?.stale && (
            <Box sx={{ mb: 2 }}>
              <Chip label="Cache refresh in progress" color="warning" variant="outlined" />
            </Box>
          )}
          {warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {warnings.map((warning) => warning.message || warning.code || 'Report warning').join(' ')}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <CancelsMetricCard label="Total Cancels" value={summary.total ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CancelsMetricCard label="Synthetic" value={summary.synthetic ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CancelsMetricCard label="Status" value={summary.status ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CancelsMetricCard label="AppSheet Matches" value={data?.appsheet?.matchedRows ?? 0} />
            </Grid>
          </Grid>

          <AppSheetMrtTable
            rows={rows}
            columns={columns}
            rowCount={rowCount}
            isLoading={isLoading}
            pagination={pagination}
            onPaginationChange={setPagination}
            title="Cancelled BACs"
          />
        </>
      )}
    </Box>
  );
};

export default CancelsReport;
